import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Phone, Store, Users, Wrench } from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  SHARED EXPORTS  (Landing.jsx imports these)                               */
/* -------------------------------------------------------------------------- */

export const LOGO =
  "https://res.cloudinary.com/dj6wdsbtx/image/upload/v1793161029/WhatsApp_Image_2026-10-27_at_8.51.41_AM-removebg-preview_xrmlsr.png";

export const NAV = [
  { href: "/", label: "Home", icon: Home },
  { href: "/about", label: "About Us", icon: Users },
  { href: "/services", label: "Services", icon: Wrench },
  { href: "/shop", label: "Shop", icon: Store },
];

// Routes that open with a full-bleed photo behind the navbar. On these the
// bar starts transparent and turns solid once you scroll. Add a path here
// whenever a new page gets a hero image.
const HERO_ROUTES = ["/", "/services"];

export function goTo(id) {
  if (id === "home") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/* -------------------------------------------------------------------------- */
/*  HELPERS                                                                   */
/* -------------------------------------------------------------------------- */

function useScrolled(offset = 40) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > offset);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [offset]);
  return scrolled;
}

/* -------------------------------------------------------------------------- */
/*  TAB SHAPE                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * A shape attached to a screen edge. Its sides flare outward with concave
 * curves so it looks like it grows out of the edge (not a floating card).
 * Fill must be a solid colour so the curved flares blend perfectly.
 */
function Tab({
  edge = "top",
  fill = "#ffffff",
  flare = 24,
  radius = 28,
  solid = true,
  className = "",
  children,
}) {
  const top = edge === "top";
  const cy = top ? "100%" : "0";
  const base = {
    position: "absolute",
    width: flare,
    height: flare,
    [top ? "top" : "bottom"]: 0,
  };
  const grad = (cx) =>
    `radial-gradient(circle ${flare}px at ${cx} ${cy}, transparent ${
      flare - 0.5
    }px, ${fill} ${flare}px)`;

  return (
    <div className="relative">
      {/* the shape itself: fades in when `solid`, so the bar can start transparent */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 transition-opacity duration-500"
        style={{
          opacity: solid ? 1 : 0,
          filter: top
            ? "drop-shadow(0 10px 16px rgba(4,26,51,0.28))"
            : "drop-shadow(0 -8px 18px rgba(4,26,51,0.35))",
        }}
      >
        <span style={{ ...base, left: -flare, background: grad("0") }} />
        <span style={{ ...base, right: -flare, background: grad("100%") }} />
        <div
          className="absolute inset-0"
          style={{
            background: fill,
            borderRadius: top
              ? `0 0 ${radius}px ${radius}px`
              : `${radius}px ${radius}px 0 0`,
          }}
        />
      </div>
      <div className={`relative ${className}`}>{children}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  NAVBAR                                                                    */
/*  Desktop: a tab that hangs from the top edge with curved, flared sides.     */
/*  Mobile / tablet: a bottom tab bar (same curved sides) + a small logo tab.  */
/*                                                                            */
/*  Lives in App.js so it shows on every route. `ready` lets the landing      */
/*  page hold the navbar back until its loader has finished.                  */
/* -------------------------------------------------------------------------- */

export default function Navbar({ ready = true }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const scrolled = useScrolled(40);
  const [hovered, setHovered] = useState(null);
  const [inContact, setInContact] = useState(false);

  const isHome = pathname === "/";
  // Pages in HERO_ROUTES have a photo behind the bar, so it starts
  // transparent and turns solid on scroll. Every other page gets the solid
  // white tab straight away so the links stay readable.
  const hasHero = HERO_ROUTES.includes(pathname);
  const solid = scrolled || !hasHero;

  const isCurrent = (href) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  // The contact section lives on the home page. From any other route we go
  // home first and Landing scrolls to it once it has mounted.
  const handleContact = () => {
    if (isHome) goTo("contact");
    else navigate("/", { state: { scrollTo: "contact" } });
  };

  // highlight "Contact" in the bottom bar while the contact section is on screen
  useEffect(() => {
    setInContact(false);
    const el = document.getElementById("contact");
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => setInContact(entry.isIntersecting),
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [pathname, ready]);

  const mobileItems = [
    ...NAV.map((n) => ({ ...n, key: n.href, kind: "link" })),
    { key: "contact", label: "Contact", icon: Phone, kind: "action" },
  ];
  const activeKey = inContact
    ? "contact"
    : NAV.find((n) => isCurrent(n.href))?.href;

  return (
    <>
      {/* ---------------- desktop: tab hanging from the top ---------------- */}
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={ready ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
        className="pointer-events-none fixed inset-x-0 top-0 z-50 hidden justify-center lg:flex"
      >
        <div className="pointer-events-auto">
          <Tab edge="top" fill="#ffffff" flare={26} radius={30} solid={solid}>
            <nav
              aria-label="Primary"
              onMouseLeave={() => setHovered(null)}
              className={`flex w-[min(74rem,calc(100vw-8rem))] items-center justify-between gap-6 px-6 transition-all duration-500 xl:px-8 ${
                solid ? "pb-2.5 pt-1.5" : "pb-3 pt-2"
              }`}
            >
              <Link
                to="/"
                aria-label="MajiMaji Hub home"
                className="block shrink-0 transition-transform duration-300 hover:scale-[1.03]"
              >
                <img
                  src={LOGO}
                  alt="MajiMaji Hub"
                  className={`w-auto transition-all duration-500 ${
                    solid
                      ? "h-12"
                      : "h-16 drop-shadow-[0_2px_14px_rgba(255,255,255,0.65)]"
                  }`}
                />
              </Link>

              <div className="flex items-center gap-1">
                {NAV.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onMouseEnter={() => setHovered(item.href)}
                    onFocus={() => setHovered(item.href)}
                    onBlur={() => setHovered(null)}
                    aria-current={isCurrent(item.href) ? "page" : undefined}
                    className={`group relative rounded-xl px-3.5 py-3 text-[15px] font-semibold transition-colors duration-500 ${
                      solid ? "text-black" : "text-white"
                    }`}
                  >
                    {hovered === item.href && (
                      <motion.span
                        layoutId="nav-hover"
                        className={`absolute inset-0 rounded-xl ${
                          solid ? "bg-[#0A3568]/10" : "bg-white/20"
                        }`}
                        transition={{ type: "spring", stiffness: 420, damping: 34 }}
                      />
                    )}
                    <span className="relative flex items-center gap-2">
                      <item.icon
                        size={18}
                        className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-110"
                      />
                      {item.label}
                    </span>
                    {isCurrent(item.href) && (
                      <span
                        className={`absolute inset-x-3.5 bottom-1.5 h-[2px] rounded-full ${
                          solid ? "bg-[#1F7BE0]" : "bg-white"
                        }`}
                      />
                    )}
                  </Link>
                ))}
              </div>

              <button
                onClick={handleContact}
                className={`group inline-flex shrink-0 items-center gap-2 rounded-xl px-5 py-3 text-[15px] font-bold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${
                  solid
                    ? "bg-[#0A3568] text-white hover:bg-[#1F7BE0]"
                    : "bg-[#F2A93B] text-[#0B1F3A] hover:bg-white"
                }`}
              >
                <Phone
                  size={18}
                  className="transition-transform duration-300 group-hover:rotate-12"
                />
                Contact us
              </button>
            </nav>
          </Tab>
        </div>
      </motion.header>

      {/* ---------------- mobile / tablet: logo tab at the top ------------- */}
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={ready ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
        className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center lg:hidden"
      >
        <div className="pointer-events-auto">
          <Tab edge="top" fill="#ffffff" flare={20} radius={24} solid={solid}>
            <Link
              to="/"
              aria-label="MajiMaji Hub home"
              className="block px-6 pb-2 pt-1.5"
            >
              <img
                src={LOGO}
                alt="MajiMaji Hub"
                className={`w-auto transition-all duration-500 ${
                  solid
                    ? "h-11"
                    : "h-14 drop-shadow-[0_2px_14px_rgba(255,255,255,0.65)]"
                }`}
              />
            </Link>
          </Tab>
        </div>
      </motion.header>

      {/* ---------------- mobile / tablet: bottom tab bar ------------------ */}
      <motion.nav
        aria-label="Mobile"
        initial={{ y: 90, opacity: 0 }}
        animate={ready ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center lg:hidden"
      >
        <div className="pointer-events-auto">
          <Tab edge="bottom" fill="#072445" flare={22} radius={26}>
            <ul className="flex w-[calc(100vw-3.5rem)] max-w-md items-center justify-between px-2 pb-[calc(8px+env(safe-area-inset-bottom,0px))] pt-2">
              {mobileItems.map((item) => {
                const active = activeKey === item.key;
                const itemClass =
                  "relative flex h-11 items-center justify-center rounded-2xl px-3";
                const inner = (
                  <>
                    {active && (
                      <motion.span
                        layoutId="tab-active"
                        className="absolute inset-0 rounded-2xl bg-white"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                      />
                    )}
                    <span className="relative flex items-center gap-2">
                      <item.icon
                        size={20}
                        className={active ? "text-[#0A3568]" : "text-white/80"}
                      />
                      {active && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.1 }}
                          className="whitespace-nowrap pr-1 text-sm font-bold text-black"
                        >
                          {item.label}
                        </motion.span>
                      )}
                      {!active && <span className="sr-only">{item.label}</span>}
                    </span>
                  </>
                );

                return (
                  <motion.li
                    key={item.key}
                    layout
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  >
                    {item.kind === "link" ? (
                      <Link
                        to={item.href}
                        aria-label={item.label}
                        aria-current={active ? "page" : undefined}
                        className={itemClass}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={handleContact}
                        aria-label={item.label}
                        className={itemClass}
                      >
                        {inner}
                      </button>
                    )}
                  </motion.li>
                );
              })}
            </ul>
          </Tab>
        </div>
      </motion.nav>
    </>
  );
}